import React, { useMemo, useEffect, useRef, useState } from "react";
import { Info, Lightbulb } from "lucide-react";

/* UI bits (same look as Alerty) */
const Separator = () => (
  <div style={{ height: 1, background: "#e5e7eb", margin: "8px 0 12px" }} />
);

const Row = ({ children }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      lineHeight: 1.5,
      color: "#2563eb",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        display: "grid",
        placeItems: "center",
        borderRadius: 6,
        background: "#eff6ff",
        color: "#2563eb",
        flexShrink: 0,
      }}
    >
      <Info size={14} />
    </div>
    <div style={{ fontSize: 16, fontWeight: 600 }}>{children}</div>
  </div>
);


/** Sugestie / Informacje — paginated (1 per page) with 5s auto-rotate */
const SuggestionsInfo = ({
  title = "Sugestie i informacje",
  titleIcon: TitleIcon = Lightbulb,
  items: externalItems,
  pageSize = 1,          // <-- changed from 3 to 1
  autoRotateMs = 5000,
}) => {
  // default 5 messages (you can override via `items` prop)
  const items = useMemo(
    () =>
      externalItems ?? [
        "Im więcej danych zaimportujesz tym lepszej jakości wykresy otrzymasz",
        "Już wkrótce dodamy nowe raporty związane z rozliczeniem VAT",
        "W raporcie wiarygodności klienta możesz sprawdzić czy występuje on na giełdach wierzytelności",
        "Napisz do nas, jeśli masz jakieś uwagi lub zauważyłeś błędy",
        "Pojawiła się nowa wersja raportu Najlepsi kontrahenci – sprawdź koniecznie",
      ],
    [externalItems]
  );

  const groups = Math.max(1, Math.ceil(items.length / pageSize));
  const [groupIdx, setGroupIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (autoRotateMs <= 0 || groups <= 1) return;
    timerRef.current = setInterval(() => {
      setGroupIdx((i) => (i + 1) % groups);
    }, autoRotateMs);
    return () => clearInterval(timerRef.current);
  }, [groups, autoRotateMs]);

  const start = groupIdx * pageSize;
  const end = Math.min(start + pageSize, items.length);
  const pageItems = items.slice(start, end);

  return (
    <div style={{ background: "#fff", padding: 16 }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: "#f1f5f9",
            display: "grid",
            placeItems: "center",
            color: "#0f172a",
          }}
        >
          <TitleIcon size={16} />
        </div>
        <div style={{ fontWeight: 800, color: "#0f172a" }}>{title}</div>
      </div>

      <Separator />

      {/* current page (fixed height via placeholders) */}
      <div style={{ display: "grid", gap: 10 }}>
        {pageItems.map((txt, i) => (
          <Row key={`${groupIdx}-${i}`}>{txt}</Row>
        ))}

        {/* placeholders to keep card height fixed */}
        {Array.from({ length: Math.max(0, pageSize - pageItems.length) }).map(
          (_, i) => (
            <div key={`ph-${groupIdx}-${i}`} style={{ visibility: "hidden" }}>
              <Row>&nbsp;</Row>
            </div>
          )
        )}
      </div>

      {/* dots */}
      {groups > 1 && (
        <div
          style={{
            display: "flex",
            gap: 6,
            justifyContent: "center",
            marginTop: 12,
          }}
        >
          {Array.from({ length: groups }).map((_, i) => (
            <button
              key={i}
              onClick={() => setGroupIdx(i)}
              aria-label={`Strona ${i + 1}`}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: "none",
                cursor: "pointer",
                background: i === groupIdx ? "#2563eb" : "#e5e7eb",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestionsInfo;
